import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import { LoadingButton } from '@mui/lab';
import { Box, Button, ButtonGroup, Skeleton, Tooltip } from '@mui/material';
import Oystehr from '@oystehr/sdk';
import { enqueueSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { createDischargeSummary } from 'src/api/api';
import { dataTestIds } from 'src/constants/data-test-ids';
import { handleChangeInPersonVisitStatus } from 'src/helpers/inPersonVisitStatusUtils';
import { useApiClients } from 'src/hooks/useAppClients';
import useEvolveUser from 'src/hooks/useEvolveUser';
import { DownloadDocumentOptions } from 'src/hooks/useGetPatientDocs';
import { getInPersonVisitStatus } from 'utils/lib/utils/visitUtils';
import { useAppointmentData } from '../../stores/appointment/appointment.store';
import { DischargeDialog } from './DischargeDialog';

export const createAndOpenDischargeSummary = async (
  oystehr: Oystehr,
  appointmentId: string,
  downloadDocument: (id: string, options?: DownloadDocumentOptions) => Promise<void>,
  options?: DownloadDocumentOptions
): Promise<boolean> => {
  let documentId: string | undefined;

  try {
    documentId = (await createDischargeSummary(oystehr, { appointmentId }))?.documentId;
  } catch (error) {
    console.error('Error creating Discharge Summary:', error);
    enqueueSnackbar('Error creating Discharge Summary.', { variant: 'error' });
    options?.targetTab?.close();
    return false;
  }

  // The document is filed past this point, so a failure to open it is not a failure to create it.
  if (!documentId) {
    enqueueSnackbar(
      'Discharge summary created, but document is not accessible right now. You can find it later in the Patient Record > Review Docs.',
      { variant: 'info' }
    );
    options?.targetTab?.close();
    return true;
  }

  try {
    await downloadDocument(documentId, options);
  } catch (error) {
    console.error('Error opening Discharge Summary:', error);
    enqueueSnackbar(
      'Discharge summary created, but it could not be opened. You can find it in the Patient Record > Review Docs.',
      { variant: 'warning' }
    );
  }

  return true;
};

export const handleDischarge = async (encounterId: string, oystehr?: Oystehr): Promise<void> => {
  await handleChangeInPersonVisitStatus({ encounterId, updatedStatus: 'discharged' }, oystehr);
  enqueueSnackbar('Patient discharged successfully', { variant: 'success' });
};

export const DischargeButton: FC = () => {
  const { appointment, encounter, appointmentRefetch, resources } = useAppointmentData();
  const { oystehrZambda } = useApiClients();
  const user = useEvolveUser();
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const inPersonStatus = useMemo(
    () => appointment && getInPersonVisitStatus(appointment, encounter),
    [appointment, encounter]
  );

  const isAlreadyDischarged = inPersonStatus === 'discharged' || inPersonStatus === 'completed';

  if (!user || !encounter?.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: '20px' }} />
      </Box>
    );
  }

  const encounterId: string = encounter.id;
  const appointmentId = resources?.appointment?.id ?? appointment?.id;
  const patientId = resources?.patient?.id;

  const onDischargeClick = async (): Promise<void> => {
    setStatusLoading(true);

    try {
      await handleDischarge(encounterId, oystehrZambda);
      await appointmentRefetch();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('An error occurred. Please try again.', { variant: 'error' });
    } finally {
      setStatusLoading(false);
    }
  };

  if (isAlreadyDischarged) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <LoadingButton
          disabled
          variant="contained"
          startIcon={<CheckIcon color="inherit" />}
          data-testid={dataTestIds.progressNotePage.dischargeButton}
          sx={{ borderRadius: 100, textTransform: 'none' }}
        >
          Discharged
        </LoadingButton>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <ButtonGroup variant="contained" disabled={statusLoading || isAlreadyDischarged} sx={{ boxShadow: 'none' }}>
          <LoadingButton
            variant="contained"
            loading={statusLoading}
            onClick={onDischargeClick}
            data-testid={dataTestIds.progressNotePage.dischargeButton}
            sx={{ borderRadius: '100px', textTransform: 'none' }}
          >
            Discharge
          </LoadingButton>
          <Tooltip title="Discharge & More">
            <Button
              variant="contained"
              size="small"
              onClick={() => setDialogOpen(true)}
              data-testid={dataTestIds.dischargeDialog.openButton}
              sx={{ borderRadius: '100px' }}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Box>

      {dialogOpen && (
        <DischargeDialog
          onClose={() => setDialogOpen(false)}
          encounterId={encounterId}
          appointmentId={appointmentId}
          patientId={patientId}
        />
      )}
    </>
  );
};
